import { useAsyncData } from '@snapchat/async-data-browser';
import { useCallback, useContext, useState } from 'react';

import { AppContext } from '../AppContext';
import { logger } from '../helpers/logging';
import type { PersonaResponse } from '../utils/persona/personaTypes';
import { getPersonasCookie } from '../utils/persona/personaUtils';

const component = 'useUserBusinessProfile';

type UserBusinessProfileDecition = {
  isThinking: boolean;
  /**
   * The decision of the user's business profile.
   *
   * - `undefined` if profile has not been fetched yet
   * - `null` if profile has been fetched but did not match (no revoptimal id)
   * - Empty array if profile has been fetched and matched (has revoptimal id) but no personas
   * - Array of strings if profile has been fetched and matched (has revoptimal id) and there are
   *   personas
   */
  decision: string[] | null | undefined;
};

export type UserBusinessProfileDecider = (targetCriteria?: string[]) => UserBusinessProfileDecition;

/**
 * Hook that returns helper functions for working with a user's business profile.
 *
 * The user business profile functions, when triggerred have side-effects like reading/setting
 * cookies or making RPC requets, so they should be called conditionally on them being needed.
 */
export function useUserBusinessProfile(): UserBusinessProfileDecider {
  const { cookieManager, getPersona, personas } = useContext(AppContext);

  const [needsDecision, setNeedsDecision] = useState(false);

  const { data: personaResponse } = useAsyncData({
    skip: !needsDecision, // Skip loading if we don't need the decision.
    dataId: 'persona', // Has to be the same as in other places to hit cache.
    dataAsync: getPersona,
    onError: error => logger.logError({ component, error }),
  });

  const getUserBusinessProfile = useCallback<UserBusinessProfileDecider>(() => {
    // Check for business personas in app state first
    if (personas?.biz !== undefined) {
      return { isThinking: false, decision: personas.biz };
    }

    const personasCookieValue = getPersonasCookie(cookieManager);

    // If there are business personas in the cookie, return them
    if (personasCookieValue?.biz !== undefined) {
      return { isThinking: false, decision: personasCookieValue.biz };
    }

    // Signals to load the user's business profile - only activate if not found in app state or cookie
    if (!needsDecision) {
      setNeedsDecision(true);
    }

    return getUserBusinessProfileImpl(personaResponse);
  }, [cookieManager, needsDecision, personaResponse, personas]);

  return getUserBusinessProfile;
}

/**
 * Internal helper function with the business logic.
 *
 * Extracted for testing purposes.
 */
export const getUserBusinessProfileImpl = (
  personaResponse: PersonaResponse | undefined
): UserBusinessProfileDecition => {
  // Profile not loaded yet. Don't make a decision yet
  if (!personaResponse) {
    return { isThinking: true, decision: undefined };
  }

  // If the profile has been retrieved, but there is no matches
  if (!personaResponse?.matches?.length) {
    return { isThinking: false, decision: null };
  }

  // If the profile has been retrieved, return the business personas
  const decision = [...personaResponse.matches];

  return { isThinking: false, decision };
};
